import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    detectContactsImportFile,
    detectPastedContactsImport,
    detectSourceContactsImport,
    executeContactsImport,
    getContactsImportHistory,
    getContactsImportHistoryDetail,
    previewContactsImport,
    searchSourceImportPeople
} from '../api/api';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription
} from "./ui/sheet";
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from './ui/select';
import {
    AlertCircle,
    ArrowRight,
    CheckCircle2,
    CircleSlash,
    Clipboard,
    Columns3,
    Database,
    FileSpreadsheet,
    History,
    Loader2,
    RefreshCw,
    Search,
    ShieldCheck,
    TableProperties,
    UploadCloud,
    Users,
    Wand2,
    X
} from 'lucide-react';
import { CANONICAL_IMPORT_FIELDS, CANONICAL_IMPORT_GROUPS } from '../constants/contactImportFields.js';
import {
    buildInitialMappingState,
    buildValidationReadyMappingPayload,
    createIgnoredState,
    createMappedState,
    createUnresolvedState,
    getFieldUsage,
    getMappingReadiness
} from '../utils/contactImportMapping.js';
import { formatDateTime } from '../lib/i18n';

const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const IMPORT_METHODS = [
    {
        key: 'file_upload',
        labelKey: 'csvUpload.methods.fileUploadLabel',
        descriptionKey: 'csvUpload.methods.fileUploadDescription',
        icon: FileSpreadsheet
    },
    {
        key: 'paste_table',
        labelKey: 'csvUpload.methods.pasteTableLabel',
        descriptionKey: 'csvUpload.methods.pasteTableDescription',
        icon: Clipboard
    },
    {
        key: 'source_import',
        labelKey: 'csvUpload.methods.sourceImportLabel',
        descriptionKey: 'csvUpload.methods.sourceImportDescription',
        icon: Users
    }
];

function formatBytes(bytes = 0) {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatTimestamp(value, t, language) {
    if (!value) return t('csvUpload.unknownTime');
    return formatDateTime(value, {}, language);
}

function formatMethodLabel(method, t) {
    const entry = IMPORT_METHODS.find((item) => item.key === method);
    return entry ? t(entry.labelKey) : method;
}

function buildFriendlyError(error, t) {
    const responseData = error?.response?.data;
    const details = responseData?.details;
    return {
        title: responseData?.message || error?.message || t('csvUpload.errors.stepFailed'),
        detail: details?.parserMessage || details?.blockerCategory || null
    };
}

function hintBadges(column) {
    const hints = column?.patternHints || {};
    return [
        hints.emailLike && 'Email-like',
        hints.phoneLike && 'Phone-like',
        hints.urlLike && 'URL-like',
        hints.linkedinLike && 'LinkedIn-like',
        hints.companyLike && 'Company-like',
        hints.nameLike && 'Name-like',
        hints.titleLike && 'Title-like',
        hints.locationLike && 'Location-like',
        column?.likelyIdentityCapable && 'Identity-capable'
    ].filter(Boolean);
}

function EntryMethodCard({ method, active, onClick }) {
    const Icon = method.icon;
    const { t } = useTranslation();
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-[1.5rem] border p-5 text-left transition-all ${active ? 'border-primary bg-primary/[0.05] shadow-sm' : 'border-border/60 bg-background hover:border-primary/40'}`}
        >
            <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl border ${active ? 'border-primary/20 bg-primary/10 text-primary' : 'border-border bg-muted/30 text-foreground'}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <div>
                    <div className="text-sm font-black tracking-tight">{t(method.labelKey)}</div>
                    <div className="text-xs text-muted-foreground mt-1">{t(method.descriptionKey)}</div>
                </div>
            </div>
        </button>
    );
}

export default function ContactImportIntakeModal({ isOpen, onClose }) {
    const { t, i18n } = useTranslation();
    const inputRef = useRef(null);
    const [importMethod, setImportMethod] = useState('file_upload');
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [pasteText, setPasteText] = useState('');
    const [sourceQuery, setSourceQuery] = useState('');
    const [sourceResults, setSourceResults] = useState([]);
    const [selectedSourceIds, setSelectedSourceIds] = useState([]);
    const [historyItems, setHistoryItems] = useState([]);
    const [historyDetail, setHistoryDetail] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
    const [sourceLoading, setSourceLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(null);
    const [mappingState, setMappingState] = useState({});
    const [validationPayloadPrepared, setValidationPayloadPrepared] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [executeLoading, setExecuteLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const fileSummary = useMemo(() => {
        if (!file) return null;
        return `${file.name} • ${formatBytes(file.size)}`;
    }, [file]);

    const columns = useMemo(() => payload?.detectedColumns || [], [payload]);
    const fieldUsage = useMemo(() => getFieldUsage(mappingState), [mappingState]);
    const readiness = useMemo(() => getMappingReadiness(mappingState, columns), [mappingState, columns]);
    const validationReadyPayload = useMemo(
        () => (payload ? buildValidationReadyMappingPayload(payload, mappingState) : null),
        [payload, mappingState]
    );

    const previewGroups = useMemo(() => {
        const rows = previewData?.rows || [];
        return {
            ready: rows.filter((row) => row.state === 'READY_CREATE'),
            skipped: rows.filter((row) => row.state === 'SKIPPED_DUPLICATE_BATCH' || row.state === 'SKIPPED_EXISTING'),
            blocked: rows.filter((row) => row.state === 'BLOCKED_ERROR')
        };
    }, [previewData]);

    const groupedFields = CANONICAL_IMPORT_GROUPS.map((group) => ({
        group,
        fields: CANONICAL_IMPORT_FIELDS.filter((field) => field.category === group && !field.deferred)
    }));

    const clearFlowState = () => {
        setError(null);
        setPayload(null);
        setMappingState({});
        setValidationPayloadPrepared(false);
        setPreviewData(null);
        setImportResult(null);
        setLoading(false);
        setPreviewLoading(false);
        setExecuteLoading(false);
    };

    const resetMethodInputs = () => {
        setFile(null);
        setPasteText('');
        setSourceQuery('');
        setSourceResults([]);
        setSelectedSourceIds([]);
        setDragActive(false);
        if (inputRef.current) inputRef.current.value = '';
    };

    const resetState = () => {
        setImportMethod('file_upload');
        clearFlowState();
        resetMethodInputs();
        setHistoryDetail(null);
    };

    const handleClose = (nextOpen) => {
        if (!nextOpen) {
            resetState();
            onClose?.();
        }
    };

    const loadHistory = async () => {
        setHistoryLoading(true);
        try {
            const response = await getContactsImportHistory();
            setHistoryItems(response.data?.data || []);
        } catch (historyError) {
            setHistoryItems([]);
        } finally {
            setHistoryLoading(false);
        }
    };

// eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!isOpen) return;
        loadHistory();
    }, [isOpen]);

// eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (payload?.detectedColumns?.length) {
            setMappingState(buildInitialMappingState(payload.detectedColumns));
        } else {
            setMappingState({});
        }
    }, [payload]);

    const validateSelectedFile = (selectedFile) => {
        const lowerName = selectedFile?.name?.toLowerCase() || '';
        if (!selectedFile) return t('csvUpload.errors.selectFileContinue');
        if (!ACCEPTED_EXTENSIONS.some((ext) => lowerName.endsWith(ext))) {
            return t('csvUpload.errors.unsupportedFile');
        }
        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            return t('csvUpload.errors.fileTooLarge');
        }
        return null;
    };

    const setSelectedFile = (selectedFile) => {
        const validationError = validateSelectedFile(selectedFile);
        clearFlowState();
        setError(validationError ? { title: validationError } : null);
        setFile(validationError ? null : selectedFile);
    };

    const handleUpload = async () => {
        if (!file) {
            setError({ title: t('csvUpload.errors.selectUploadContinue') });
            return;
        }

        setLoading(true);
        setError(null);
        setPayload(null);

        try {
            const response = await detectContactsImportFile(file);
            setPayload(response.data);
        } catch (uploadError) {
            setError(buildFriendlyError(uploadError, t));
        } finally {
            setLoading(false);
        }
    };

    const handlePasteIntake = async () => {
        if (!pasteText.trim()) {
            setError({ title: t('csvUpload.errors.pasteHeaders') });
            return;
        }

        setLoading(true);
        setError(null);
        setPayload(null);

        try {
            const response = await detectPastedContactsImport(pasteText);
            setPayload(response.data);
        } catch (pasteError) {
            setError(buildFriendlyError(pasteError, t));
        } finally {
            setLoading(false);
        }
    };

    const handleSourceSearch = async () => {
        setSourceLoading(true);
        setError(null);

        try {
            const response = await searchSourceImportPeople({
                q: sourceQuery,
                page: 1,
                limit: 15,
                hasEmail: true
            });
            setSourceResults(response.data?.results || response.data?.data || []);
        } catch (searchError) {
            setSourceResults([]);
            setError(buildFriendlyError(searchError, t));
        } finally {
            setSourceLoading(false);
        }
    };

    const handleSourceIntake = async () => {
        if (selectedSourceIds.length === 0) {
            setError({ title: t('csvUpload.errors.selectSourceContact') });
            return;
        }

        setLoading(true);
        setError(null);
        setPayload(null);

        try {
            const response = await detectSourceContactsImport(selectedSourceIds);
            setPayload(response.data);
        } catch (sourceError) {
            setError(buildFriendlyError(sourceError, t));
        } finally {
            setLoading(false);
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);
        const droppedFile = event.dataTransfer?.files?.[0];
        if (droppedFile) setSelectedFile(droppedFile);
    };

    const handleMethodChange = (nextMethod) => {
        setImportMethod(nextMethod);
        clearFlowState();
        setError(null);
    };

    const getFieldDisabled = (fieldKey, currentColumnIndex) => {
        const currentMapping = mappingState[currentColumnIndex];
        const field = CANONICAL_IMPORT_FIELDS.find((entry) => entry.key === fieldKey);
        if (!field || field.allowMultipleSources) return false;
        const currentAssignedKey = currentMapping?.state === 'mapped' ? currentMapping.targetFieldKey : null;
        return fieldUsage[fieldKey] > 0 && currentAssignedKey !== fieldKey;
    };

    const handleMapField = (columnIndex, nextFieldKey) => {
        setMappingState((current) => ({
            ...current,
            [columnIndex]: createMappedState(current[columnIndex], nextFieldKey, 'manual')
        }));
        setValidationPayloadPrepared(false);
        setPreviewData(null);
        setImportResult(null);
    };

    const handleIgnoreColumn = (columnIndex) => {
        setMappingState((current) => ({
            ...current,
            [columnIndex]: createIgnoredState(current[columnIndex])
        }));
        setValidationPayloadPrepared(false);
        setPreviewData(null);
        setImportResult(null);
    };

    const handleResetColumn = (columnIndex) => {
        setMappingState((current) => ({
            ...current,
            [columnIndex]: createUnresolvedState(current[columnIndex])
        }));
        setValidationPayloadPrepared(false);
        setPreviewData(null);
        setImportResult(null);
    };

    const restoreAutoMatches = () => {
        if (!payload?.detectedColumns?.length) return;
        setMappingState(buildInitialMappingState(payload.detectedColumns));
        setValidationPayloadPrepared(false);
        setPreviewData(null);
        setImportResult(null);
    };

    const handlePreviewImport = async () => {
        if (!payload || !validationReadyPayload || !readiness.canProceed) return;
        setPreviewLoading(true);
        setError(null);
        setImportResult(null);

        try {
            const response = await previewContactsImport(payload, validationReadyPayload);
            setPreviewData(response.data);
        } catch (previewError) {
            setPreviewData(null);
            setError(buildFriendlyError(previewError, t));
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleExecuteImport = async () => {
        if (!payload || !validationReadyPayload || !previewData?.summary?.canProceed) return;
        setExecuteLoading(true);
        setError(null);

        try {
            const response = await executeContactsImport(payload, validationReadyPayload);
            setImportResult(response.data);
            await loadHistory();
        } catch (executeError) {
            setImportResult(null);
            setError(buildFriendlyError(executeError, t));
        } finally {
            setExecuteLoading(false);
        }
    };

    const toggleSourceSelection = (personId) => {
        setSelectedSourceIds((current) => (
            current.includes(personId)
                ? current.filter((id) => id !== personId)
                : [...current, personId]
        ));
    };

    const openHistoryDetail = async (runId) => {
        setHistoryDetailLoading(true);
        try {
            const response = await getContactsImportHistoryDetail(runId);
            setHistoryDetail(response.data);
        } catch (detailError) {
            setHistoryDetail(null);
            setError(buildFriendlyError(detailError, t));
        } finally {
            setHistoryDetailLoading(false);
        }
    };

    const summaryLabel = payload?.importMethod === 'paste_table'
        ? t('csvUpload.summary.pastedRowsReady')
        : payload?.importMethod === 'source_import'
            ? t('csvUpload.summary.sourceRowsReady')
            : t('csvUpload.summary.dataRowsReady');

    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent side="right" className="sm:max-w-[1240px] max-h-[92vh] overflow-hidden p-0">
                <SheetHeader className="px-6 py-5 border-b border-border bg-muted/20">
                    <SheetTitle className="flex items-center gap-3 text-xl font-black tracking-tight">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                            <UploadCloud className="w-5 h-5" />
                        </div>
                        {t('csvUpload.dialogTitle')}
                    </SheetTitle>
                    <SheetDescription className="text-sm max-w-3xl">
                        {t('csvUpload.dialogDescription')}
                    </SheetDescription>
                </SheetHeader>

                <div className="p-6 overflow-y-auto space-y-6">
                    <Card className="border-border/60 shadow-sm">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-base font-black tracking-tight">
                                <UploadCloud className="w-4 h-4 text-primary" />
                                {t('csvUpload.chooseMethod')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                {IMPORT_METHODS.map((method) => (
                                    <EntryMethodCard
                                        key={method.key}
                                        method={method}
                                        active={importMethod === method.key}
                                        onClick={() => handleMethodChange(method.key)}
                                    />
                                ))}
                            </div>

                            {importMethod === 'file_upload' && (
                                <div
                                    className={`border-2 border-dashed rounded-[1.75rem] p-8 transition-all text-center ${dragActive ? 'border-primary bg-primary/5' : 'border-border/70 bg-muted/20'} ${file ? 'border-primary/40 bg-primary/[0.03]' : ''}`}
                                    onDragEnter={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setDragActive(true);
                                    }}
                                    onDragOver={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setDragActive(true);
                                    }}
                                    onDragLeave={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setDragActive(false);
                                    }}
                                    onDrop={handleDrop}
                                >
                                    <input
                                        ref={inputRef}
                                        type="file"
                                        accept=".csv,.xlsx,.xls"
                                        className="hidden"
                                        onChange={(event) => setSelectedFile(event.target.files?.[0])}
                                    />
                                    <div className="w-16 h-16 rounded-2xl bg-background border border-border mx-auto mb-4 flex items-center justify-center shadow-sm">
                                        <FileSpreadsheet className="w-8 h-8 text-primary" />
                                    </div>
                                    <h3 className="text-lg font-black tracking-tight text-foreground">{t('csvUpload.uploadSpreadsheet')}</h3>
                                    <p className="text-sm text-muted-foreground max-w-xl mx-auto mt-2">
                                        {t('csvUpload.acceptedTypes')}
                                    </p>
                                    <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
                                        <Button type="button" onClick={() => inputRef.current?.click()} className="rounded-xl font-black text-xs uppercase tracking-widest">
                                            {t('csvUpload.chooseFile')}
                                        </Button>
                                        {file && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => {
                                                    resetMethodInputs();
                                                    clearFlowState();
                                                }}
                                                className="rounded-xl font-black text-xs uppercase tracking-widest"
                                            >
                                                <X className="w-4 h-4 mr-2" /> {t('actions.clear')}
                                            </Button>
                                        )}
                                        <Button type="button" onClick={handleUpload} disabled={!file || loading} className="rounded-xl font-black text-xs uppercase tracking-widest">
                                            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                                            {t('csvUpload.detectColumns')}
                                        </Button>
                                    </div>
                                    {fileSummary && (
                                        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-xs font-bold text-foreground">
                                            <FileSpreadsheet className="w-3.5 h-3.5 text-primary" />
                                            {fileSummary}
                                        </div>
                                    )}
                                </div>
                            )}

                            {importMethod === 'paste_table' && (
                                <div className="rounded-[1.75rem] border border-border/70 bg-muted/20 p-6 space-y-4">
                                    <div className="flex items-start gap-3">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border bg-background text-primary">
                                            <Clipboard className="h-6 w-6" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-black tracking-tight text-foreground">{t('csvUpload.pasteGrid')}</h3>
                                            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                                                {t('csvUpload.pasteDescription')}
                                            </p>
                                        </div>
                                    </div>
                                    <Textarea
                                        value={pasteText}
                                        onChange={(event) => {
                                            setPasteText(event.target.value);
                                            clearFlowState();
                                        }}
                                        placeholder={`First Name\tLast Name\tWork Email\nAda\tLovelace\tada@example.com`}
                                        className="min-h-[180px] resize-y bg-background"
                                    />
                                    <div className="flex flex-wrap gap-3">
                                        <Button type="button" onClick={handlePasteIntake} disabled={!pasteText.trim() || loading} className="rounded-xl font-black text-xs uppercase tracking-widest">
                                            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Columns3 className="w-4 h-4 mr-2" />}
                                            {t('csvUpload.detectPastedColumns')}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => {
                                                setPasteText('');
                                                clearFlowState();
                                            }}
                                            className="rounded-xl font-black text-xs uppercase tracking-widest"
                                        >
                                            <X className="w-4 h-4 mr-2" /> Clear
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {importMethod === 'source_import' && (
                                <div className="rounded-[1.75rem] border border-border/70 bg-muted/20 p-6 space-y-4">
                                    <div className="flex items-start gap-3">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border bg-background text-primary">
                                            <Users className="h-6 w-6" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-black tracking-tight text-foreground">{t('csvUpload.importFromStructuredSource')}</h3>
                                            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                                                {t('csvUpload.sourceDescription')}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col md:flex-row gap-3">
                                        <Input
                                            value={sourceQuery}
                                            onChange={(event) => setSourceQuery(event.target.value)}
                                            placeholder={t('csvUpload.searchSourcePlaceholder')}
                                            className="bg-background"
                                        />
                                        <Button type="button" onClick={handleSourceSearch} disabled={sourceLoading} className="rounded-xl font-black text-xs uppercase tracking-widest">
                                            {sourceLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                                            {t('csvUpload.searchSource')}
                                        </Button>
                                    </div>

                                    <div className="rounded-2xl border border-border/60 bg-background max-h-[280px] overflow-y-auto">
                                        {sourceResults.length === 0 ? (
                                            <div className="p-4 text-sm text-muted-foreground">
                                                {t('csvUpload.runSourceSearch')}
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-border/50">
                                                {sourceResults.map((person) => {
                                                    const id = person._id;
                                                    const checked = selectedSourceIds.includes(id);
                                                    return (
                                                        <label key={id} className="flex items-start gap-3 p-4 cursor-pointer hover:bg-muted/20">
                                                            <Checkbox checked={checked} onCheckedChange={() => toggleSourceSelection(id)} />
                                                            <div className="min-w-0">
                                                                <div className="font-semibold text-foreground">
                                                                    {person.fullName || [person.firstName, person.lastName].filter(Boolean).join(' ') || t('csvUpload.unnamedContact')}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground mt-1">
                                                                    {[person.title, person.companyName, person.location].filter(Boolean).join(' • ') || t('csvUpload.structuredSourceContact')}
                                                                </div>
                                                                {person.canonicalEmail && (
                                                                    <div className="text-xs text-muted-foreground mt-1">{person.canonicalEmail}</div>
                                                                )}
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-3 items-center">
                                        <Button type="button" onClick={handleSourceIntake} disabled={selectedSourceIds.length === 0 || loading} className="rounded-xl font-black text-xs uppercase tracking-widest">
                                            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                                            {t('csvUpload.prepareSelectedContacts')}
                                        </Button>
                                        <div className="text-xs text-muted-foreground">
                                            {t('csvUpload.selectedSourceCount', { count: selectedSourceIds.length })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {error && (
                        <Alert variant="destructive" className="border-destructive/30 bg-destructive/5">
                            <AlertCircle className="w-4 h-4" />
                            <AlertTitle>{t('csvUpload.errors.importStepCouldNotContinue')}</AlertTitle>
                            <AlertDescription>
                                {error.title}
                                {error.detail ? ` (${error.detail})` : ''}
                            </AlertDescription>
                        </Alert>
                    )}

                    <Card className="border-border/60 shadow-sm">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-base font-black tracking-tight">
                                <History className="w-4 h-4 text-primary" />
                                {t('csvUpload.historyTitle')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 xl:grid-cols-[1.25fr_1fr] gap-4">
                            <div className="rounded-2xl border border-border/60 bg-background max-h-[320px] overflow-y-auto">
                                {historyLoading ? (
                                    <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" /> {t('csvUpload.loadingHistory')}
                                    </div>
                                ) : historyItems.length === 0 ? (
                                    <div className="p-4 text-sm text-muted-foreground">
                                        {t('csvUpload.noHistory')}
                                    </div>
                                ) : (
                                    <div className="divide-y divide-border/50">
                                        {historyItems.map((run) => (
                                            <button
                                                type="button"
                                                key={run.id}
                                                onClick={() => openHistoryDetail(run.id)}
                                                className="w-full p-4 text-left hover:bg-muted/20 transition-colors"
                                            >
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <Badge variant={run.status === 'COMPLETED' ? 'success' : run.status === 'PARTIAL' ? 'warning' : run.status === 'FAILED' ? 'destructive' : 'outline'}>
                                                        {run.status}
                                                    </Badge>
                                                    <span className="text-sm font-bold text-foreground">{run.label || formatMethodLabel(run.importMethod, t)}</span>
                                                    <span className="text-xs text-muted-foreground">{formatMethodLabel(run.importMethod, t)}</span>
                                                </div>
                                                <div className="mt-2 text-xs text-muted-foreground">
                                                    {formatTimestamp(run.createdAt, t, i18n.resolvedLanguage)}
                                                    {run.actor?.name ? ` • ${run.actor.name}` : ''}
                                                </div>
                                                <div className="mt-2 text-xs text-muted-foreground">
                                                    {t('csvUpload.historySummary', { created: run.summary?.created || 0, skipped: (run.summary?.skippedExisting || 0) + (run.summary?.skippedDuplicateBatch || 0), blocked: run.summary?.blockedError || 0 })}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4 min-h-[220px]">
                                {historyDetailLoading ? (
                                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" /> {t('csvUpload.loadingImportDetail')}
                                    </div>
                                ) : historyDetail ? (
                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <Badge variant={historyDetail.status === 'COMPLETED' ? 'success' : historyDetail.status === 'PARTIAL' ? 'warning' : historyDetail.status === 'FAILED' ? 'destructive' : 'outline'}>
                                                    {historyDetail.status}
                                                </Badge>
                                                <span className="font-bold text-foreground">{historyDetail.label || formatMethodLabel(historyDetail.importMethod, t)}</span>
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1">
                                                {formatTimestamp(historyDetail.createdAt, t, i18n.resolvedLanguage)} • {formatMethodLabel(historyDetail.importMethod, t)}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="rounded-xl border border-border/60 bg-background p-3">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Created</div>
                                                <div className="text-2xl font-black tracking-tight mt-1">{historyDetail.summary?.created || 0}</div>
                                            </div>
                                            <div className="rounded-xl border border-border/60 bg-background p-3">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Skipped</div>
                                                <div className="text-2xl font-black tracking-tight mt-1">{(historyDetail.summary?.skippedExisting || 0) + (historyDetail.summary?.skippedDuplicateBatch || 0)}</div>
                                            </div>
                                            <div className="rounded-xl border border-border/60 bg-background p-3">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Blocked</div>
                                                <div className="text-2xl font-black tracking-tight mt-1">{historyDetail.summary?.blockedError || 0}</div>
                                            </div>
                                            <div className="rounded-xl border border-border/60 bg-background p-3">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Warnings</div>
                                                <div className="text-2xl font-black tracking-tight mt-1">{historyDetail.summary?.warningCount || 0}</div>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">Sample results</div>
                                            <div className="space-y-2 max-h-[160px] overflow-y-auto">
                                                {(historyDetail.sampleRows || []).slice(0, 10).map((row) => (
                                                    <div key={`${historyDetail.id}-${row.rowNumber}`} className="rounded-xl border border-border/60 bg-background p-3">
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant={row.state === 'CREATED' ? 'success' : row.state?.includes('BLOCKED') || row.state === 'ERROR' ? 'destructive' : 'outline'}>
                                                                {row.state}
                                                            </Badge>
                                                            <span className="text-xs font-black uppercase tracking-widest text-muted-foreground">Row {row.rowNumber}</span>
                                                        </div>
                                                        <div className="text-xs text-muted-foreground mt-2">
                                                            {[...(row.reasons || []), ...(row.warnings || [])].join(' • ') || 'Processed without extra notes'}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-sm text-muted-foreground">
                                        Open a recent import to inspect its summary and sample row outcomes.
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {payload && (
                        <div className="space-y-6 animate-in fade-in duration-300">
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                <Card className="border-border/60 shadow-sm">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Parsed Rows</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-3xl font-black tracking-tight">{payload.totalParsedRows}</div>
                                        <div className="text-xs text-muted-foreground mt-1">{summaryLabel}</div>
                                    </CardContent>
                                </Card>
                                <Card className="border-border/60 shadow-sm">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Mapped Columns</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-3xl font-black tracking-tight">{readiness.mappedCount}</div>
                                        <div className="text-xs text-muted-foreground mt-1">Explicitly assigned to canonical fields</div>
                                    </CardContent>
                                </Card>
                                <Card className="border-border/60 shadow-sm">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Unresolved Columns</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-3xl font-black tracking-tight">{readiness.unresolvedCount}</div>
                                        <div className="text-xs text-muted-foreground mt-1">Still require an explicit map or ignore decision</div>
                                    </CardContent>
                                </Card>
                                <Card className="border-border/60 shadow-sm">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Import Method</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-xl font-black tracking-tight">{formatMethodLabel(payload.importMethod)}</div>
                                        <div className="text-xs text-muted-foreground mt-1">{payload.sheet?.name || payload.sourceMeta?.label || 'Shared pipeline intake'}</div>
                                    </CardContent>
                                </Card>
                                <Card className="border-border/60 shadow-sm">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Identity Readiness</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-3xl font-black tracking-tight">{readiness.identitySatisfied ? 'Ready' : 'Missing'}</div>
                                        <div className="text-xs text-muted-foreground mt-1">Checks whether mapped fields can satisfy a valid identity pathway</div>
                                    </CardContent>
                                </Card>
                            </div>

                            {payload.warnings?.length > 0 && (
                                <Alert className="border-amber-500/30 bg-amber-500/5">
                                    <AlertCircle className="w-4 h-4 text-amber-600" />
                                    <AlertTitle className="text-amber-800">Intake warnings</AlertTitle>
                                    <AlertDescription className="space-y-1 text-amber-900">
                                        {payload.warnings.map((warning) => (
                                            <div key={warning}>{warning}</div>
                                        ))}
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6">
                                <Card className="border-border/60 shadow-sm">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-base font-black tracking-tight">
                                            <ShieldCheck className="w-4 h-4 text-primary" />
                                            Mapping readiness
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <Alert className={readiness.canProceed ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}>
                                            {readiness.canProceed ? <ShieldCheck className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-amber-600" />}
                                            <AlertTitle className={readiness.canProceed ? 'text-emerald-800' : 'text-amber-800'}>
                                                {readiness.canProceed ? 'Ready for validation and preview' : 'More mapping decisions are required'}
                                            </AlertTitle>
                                            <AlertDescription className={readiness.canProceed ? 'text-emerald-900' : 'text-amber-900'}>
                                                {readiness.canProceed
                                                    ? 'Every detected column has been explicitly mapped or ignored, and at least one valid identity pathway is available.'
                                                    : readiness.missingIdentityMessage || 'Resolve or ignore every remaining column before continuing.'}
                                            </AlertDescription>
                                        </Alert>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {readiness.identityPathways.map((pathway) => (
                                                <div key={pathway.key} className={`rounded-2xl border p-4 ${pathway.satisfied ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-border/60 bg-muted/20'}`}>
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant={pathway.satisfied ? 'success' : 'outline'}>
                                                            {pathway.satisfied ? 'Satisfied' : 'Not yet'}
                                                        </Badge>
                                                        <span className="text-sm font-bold text-foreground">{pathway.label}</span>
                                                    </div>
                                                    {!pathway.satisfied && (
                                                        <div className="mt-2 text-xs text-muted-foreground">
                                                            Missing: {pathway.missingFields.join(', ')}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        {readiness.lowConfidenceCount > 0 && (
                                            <Alert className="border-sky-500/30 bg-sky-500/5">
                                                <Wand2 className="w-4 h-4 text-sky-600" />
                                                <AlertTitle className="text-sky-800">Review medium-confidence suggestions</AlertTitle>
                                                <AlertDescription className="text-sky-900">
                                                    {readiness.lowConfidenceCount} auto-mapped column{readiness.lowConfidenceCount > 1 ? 's are' : ' is'} still based on a bounded suggestion rather than a guaranteed match.
                                                </AlertDescription>
                                            </Alert>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card className="border-border/60 shadow-sm">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-base font-black tracking-tight">
                                            <Database className="w-4 h-4 text-primary" />
                                            Mapping controls
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <div className="text-sm text-muted-foreground">
                                            Each detected column must remain visible until it is mapped or explicitly ignored. Unknown columns are never silently dropped.
                                        </div>
                                        <div className="flex flex-wrap gap-3">
                                            <Button type="button" variant="outline" onClick={restoreAutoMatches} className="rounded-xl font-black text-xs uppercase tracking-widest">
                                                <RefreshCw className="w-4 h-4 mr-2" /> Restore Suggestions
                                            </Button>
                                            <Button
                                                type="button"
                                                onClick={() => setValidationPayloadPrepared(true)}
                                                disabled={!readiness.canProceed}
                                                className="rounded-xl font-black text-xs uppercase tracking-widest"
                                            >
                                                <ArrowRight className="w-4 h-4 mr-2" /> Prepare Validation Payload
                                            </Button>
                                            <Button
                                                type="button"
                                                onClick={handlePreviewImport}
                                                disabled={!readiness.canProceed || previewLoading}
                                                className="rounded-xl font-black text-xs uppercase tracking-widest"
                                            >
                                                {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TableProperties className="w-4 h-4 mr-2" />}
                                                Generate Preview
                                            </Button>
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Duplicate target assignments are blocked for single-value canonical fields. Multi-source mapping is reserved for `Tags` and `Notes`.
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            <Card className="border-border/60 shadow-sm">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base font-black tracking-tight">
                                        <Columns3 className="w-4 h-4 text-primary" />
                                        Column mapping workspace
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="overflow-x-auto rounded-2xl border border-border/60">
                                        <table className="min-w-full text-sm">
                                            <thead className="bg-muted/30">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground">Column</th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground">Samples</th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground">Hints</th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground">Suggested Match</th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground">Map To</th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground">State</th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border/50">
                                                {columns.map((column) => {
                                                    const mapping = mappingState[column.columnIndex] || {
                                                        state: 'unresolved',
                                                        targetFieldKey: null,
                                                        suggestion: null
                                                    };
                                                    const suggestion = mapping.suggestion;
                                                    return (
                                                        <tr key={`${column.columnIndex}-${column.resolvedLabel}`} className={`align-top ${mapping.state === 'unresolved' ? 'bg-amber-500/[0.03]' : ''}`}>
                                                            <td className="px-4 py-4">
                                                                <div className="font-bold text-foreground">{column.resolvedLabel}</div>
                                                                <div className="text-xs text-muted-foreground mt-1">
                                                                    Raw: {column.originalLabel || 'Blank header'} • Normalized: {column.normalizedLabel || 'n/a'}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <div className="flex flex-wrap gap-2">
                                                                    {column.sampleValues?.length > 0 ? column.sampleValues.map((sample) => (
                                                                        <Badge key={`${column.resolvedLabel}-${sample}`} variant="outline" className="max-w-[240px] truncate">
                                                                            {sample}
                                                                        </Badge>
                                                                    )) : (
                                                                        <span className="text-xs text-muted-foreground">No non-empty sample values</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <div className="flex flex-wrap gap-2">
                                                                    <Badge variant={column.heuristicConfidence === 'high' ? 'default' : 'outline'}>
                                                                        {column.heuristicConfidence} confidence
                                                                    </Badge>
                                                                    {hintBadges(column).map((label) => (
                                                                        <Badge key={`${column.resolvedLabel}-${label}`} variant="secondary">
                                                                            {label}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                {suggestion?.fieldKey ? (
                                                                    <div className="space-y-2">
                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                            <Badge variant={suggestion.confidence === 'high' ? 'success' : suggestion.confidence === 'medium' ? 'warning' : 'outline'}>
                                                                                {suggestion.confidence} suggestion
                                                                            </Badge>
                                                                            <span className="text-sm font-bold text-foreground">
                                                                                {CANONICAL_IMPORT_FIELDS.find((field) => field.key === suggestion.fieldKey)?.label || suggestion.fieldKey}
                                                                            </span>
                                                                        </div>
                                                                        {suggestion.reasons?.length > 0 && (
                                                                            <div className="text-xs text-muted-foreground">
                                                                                {suggestion.reasons.join(' • ')}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-xs text-muted-foreground">No strong suggestion yet</span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-4 min-w-[240px]">
                                                                <Select
                                                                    value={mapping.targetFieldKey || undefined}
                                                                    onValueChange={(value) => handleMapField(column.columnIndex, value)}
                                                                >
                                                                    <SelectTrigger className="rounded-xl border-2 border-border/60 bg-background shadow-sm">
                                                                        <SelectValue placeholder="Choose a canonical field" />
                                                                    </SelectTrigger>
                                                                    <SelectContent className="max-h-[320px]">
                                                                        {groupedFields.map(({ group, fields }, groupIndex) => (
                                                                            <React.Fragment key={group}>
                                                                                <SelectGroup>
                                                                                    <SelectLabel>{group}</SelectLabel>
                                                                                    {fields.map((field) => (
                                                                                        <SelectItem
                                                                                            key={field.key}
                                                                                            value={field.key}
                                                                                            disabled={getFieldDisabled(field.key, column.columnIndex)}
                                                                                        >
                                                                                            {field.label}
                                                                                        </SelectItem>
                                                                                    ))}
                                                                                </SelectGroup>
                                                                                {groupIndex < groupedFields.length - 1 && <SelectSeparator />}
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                <div className="mt-2 text-xs text-muted-foreground">
                                                                    Empty ratio: {Math.round((column.emptyRatio || 0) * 100)}%
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <div className="flex flex-wrap gap-2">
                                                                    <Badge variant={mapping.state === 'mapped' ? 'success' : mapping.state === 'ignored' ? 'secondary' : 'warning'}>
                                                                        {mapping.state}
                                                                    </Badge>
                                                                    {mapping.state === 'mapped' && (
                                                                        <Badge variant={mapping.confirmed ? 'info' : 'outline'}>
                                                                            {mapping.confirmed ? 'confirmed' : 'suggested'}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                {mapping.state === 'mapped' && mapping.targetFieldKey && (
                                                                    <div className="mt-2 text-xs text-muted-foreground">
                                                                        Target: {CANONICAL_IMPORT_FIELDS.find((field) => field.key === mapping.targetFieldKey)?.label || mapping.targetFieldKey}
                                                                    </div>
                                                                )}
                                                                {mapping.state === 'unresolved' && (
                                                                    <div className="mt-2 text-xs text-amber-700">
                                                                        This column stays visible until you map or ignore it.
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <div className="flex flex-wrap gap-2">
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        onClick={() => handleIgnoreColumn(column.columnIndex)}
                                                                        className="rounded-xl font-black text-[11px] uppercase tracking-widest"
                                                                    >
                                                                        <CircleSlash className="w-3.5 h-3.5 mr-1.5" /> Ignore
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        onClick={() => handleResetColumn(column.columnIndex)}
                                                                        className="rounded-xl font-black text-[11px] uppercase tracking-widest"
                                                                    >
                                                                        Reset
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-border/60 shadow-sm">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base font-black tracking-tight">
                                        <Database className="w-4 h-4 text-primary" />
                                        Validation-ready mapping output
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                            <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Mapped Assignments</div>
                                            <div className="text-3xl font-black tracking-tight mt-2">{validationReadyPayload?.mappingAssignments?.length || 0}</div>
                                        </div>
                                        <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                            <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Ignored Columns</div>
                                            <div className="text-3xl font-black tracking-tight mt-2">{validationReadyPayload?.ignoredColumns?.length || 0}</div>
                                        </div>
                                        <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                            <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Unresolved Columns</div>
                                            <div className="text-3xl font-black tracking-tight mt-2">{validationReadyPayload?.unresolvedColumns?.length || 0}</div>
                                        </div>
                                    </div>

                                    {validationPayloadPrepared ? (
                                        <div className="rounded-2xl border border-border/60 bg-background overflow-x-auto">
                                            <pre className="p-4 text-xs leading-relaxed text-foreground whitespace-pre-wrap">
                                                {JSON.stringify(validationReadyPayload, null, 2)}
                                            </pre>
                                        </div>
                                    ) : (
                                        <Alert className="border-sky-500/30 bg-sky-500/5">
                                            <ArrowRight className="w-4 h-4 text-sky-600" />
                                            <AlertTitle className="text-sky-800">Validation handoff contract</AlertTitle>
                                            <AlertDescription className="text-sky-900">
                                                When mapping is ready, freeze the current mapped, ignored, and unresolved state into the contract consumed by preview and create-only execution.
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                </CardContent>
                            </Card>

                            {previewData && (
                                <Card className="border-border/60 shadow-sm">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-base font-black tracking-tight">
                                            <ShieldCheck className="w-4 h-4 text-primary" />
                                            Import preview
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Ready to Create</div>
                                                <div className="text-3xl font-black tracking-tight mt-2">{previewData.summary.readyCreate}</div>
                                            </div>
                                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Skipped Existing</div>
                                                <div className="text-3xl font-black tracking-tight mt-2">{previewData.summary.skippedExisting}</div>
                                            </div>
                                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Skipped In Batch</div>
                                                <div className="text-3xl font-black tracking-tight mt-2">{previewData.summary.skippedDuplicateBatch}</div>
                                            </div>
                                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Blocked Rows</div>
                                                <div className="text-3xl font-black tracking-tight mt-2">{previewData.summary.blockedError}</div>
                                            </div>
                                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Warnings</div>
                                                <div className="text-3xl font-black tracking-tight mt-2">{previewData.summary.warningCount}</div>
                                            </div>
                                        </div>

                                        <Alert className={previewData.summary.canProceed ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-destructive/30 bg-destructive/5'}>
                                            {previewData.summary.canProceed ? <ShieldCheck className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-destructive" />}
                                            <AlertTitle className={previewData.summary.canProceed ? 'text-emerald-800' : 'text-destructive'}>
                                                {previewData.summary.canProceed ? 'Preview ready for import' : 'Preview found no importable rows'}
                                            </AlertTitle>
                                            <AlertDescription className={previewData.summary.canProceed ? 'text-emerald-900' : 'text-destructive'}>
                                                {previewData.summary.canProceed
                                                    ? 'Only rows marked Ready to Create will be created. Existing matches and blocked rows will be skipped.'
                                                    : 'The import cannot continue because no rows are currently eligible for creation.'}
                                            </AlertDescription>
                                        </Alert>

                                        <div className="flex flex-wrap gap-3">
                                            <Button
                                                type="button"
                                                onClick={handleExecuteImport}
                                                disabled={!previewData.summary.canProceed || executeLoading}
                                                className="rounded-xl font-black text-xs uppercase tracking-widest"
                                            >
                                                {executeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                                                Import Ready Rows
                                            </Button>
                                        </div>

                                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                            <Card className="border-border/60 shadow-none">
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-sm font-black tracking-tight">Ready to Create</CardTitle>
                                                </CardHeader>
                                                <CardContent className="space-y-3 max-h-[320px] overflow-y-auto">
                                                    {previewGroups.ready.length === 0 ? (
                                                        <div className="text-xs text-muted-foreground">No rows are currently ready.</div>
                                                    ) : (
                                                        previewGroups.ready.slice(0, 20).map((row) => (
                                                            <div key={`ready-${row.rowNumber}`} className="rounded-xl border border-border/60 p-3">
                                                                <div className="flex items-center gap-2">
                                                                    <Badge variant="success">Ready</Badge>
                                                                    <span className="text-xs font-black uppercase tracking-widest text-muted-foreground">Row {row.rowNumber}</span>
                                                                </div>
                                                                <div className="mt-2 text-sm font-semibold text-foreground">
                                                                    {row.normalized?.fullName || `${row.normalized?.firstName || ''} ${row.normalized?.lastName || ''}`.trim() || row.normalized?.workEmail || 'Unnamed row'}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground mt-1">
                                                                    {row.normalized?.workEmail || row.normalized?.linkedinUrl || row.normalized?.companyName || 'Identity derived from mapped fields'}
                                                                </div>
                                                                {row.warnings?.length > 0 && (
                                                                    <div className="mt-2 text-xs text-amber-700">{row.warnings.join(' • ')}</div>
                                                                )}
                                                            </div>
                                                        ))
                                                    )}
                                                </CardContent>
                                            </Card>

                                            <Card className="border-border/60 shadow-none">
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-sm font-black tracking-tight">Skipped</CardTitle>
                                                </CardHeader>
                                                <CardContent className="space-y-3 max-h-[320px] overflow-y-auto">
                                                    {previewGroups.skipped.length === 0 ? (
                                                        <div className="text-xs text-muted-foreground">No rows are currently skipped.</div>
                                                    ) : (
                                                        previewGroups.skipped.slice(0, 20).map((row) => (
                                                            <div key={`skip-${row.rowNumber}`} className="rounded-xl border border-border/60 p-3">
                                                                <div className="flex items-center gap-2">
                                                                    <Badge variant="outline">Skipped</Badge>
                                                                    <span className="text-xs font-black uppercase tracking-widest text-muted-foreground">Row {row.rowNumber}</span>
                                                                </div>
                                                                <div className="mt-2 text-xs text-muted-foreground">
                                                                    {row.reasons?.join(' • ') || 'Skipped in preview'}
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </CardContent>
                                            </Card>

                                            <Card className="border-border/60 shadow-none">
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-sm font-black tracking-tight">Blocked</CardTitle>
                                                </CardHeader>
                                                <CardContent className="space-y-3 max-h-[320px] overflow-y-auto">
                                                    {previewGroups.blocked.length === 0 ? (
                                                        <div className="text-xs text-muted-foreground">No blocked rows.</div>
                                                    ) : (
                                                        previewGroups.blocked.slice(0, 20).map((row) => (
                                                            <div key={`blocked-${row.rowNumber}`} className="rounded-xl border border-border/60 p-3">
                                                                <div className="flex items-center gap-2">
                                                                    <Badge variant="destructive">Blocked</Badge>
                                                                    <span className="text-xs font-black uppercase tracking-widest text-muted-foreground">Row {row.rowNumber}</span>
                                                                </div>
                                                                <div className="mt-2 text-xs text-destructive">
                                                                    {row.reasons?.join(' • ') || 'Blocked by validation'}
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {importResult && (
                                <Card className="border-border/60 shadow-sm">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-base font-black tracking-tight">
                                            <Database className="w-4 h-4 text-primary" />
                                            Import result summary
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <Alert className={importResult.summary.partialSuccess ? 'border-amber-500/30 bg-amber-500/5' : 'border-emerald-500/30 bg-emerald-500/5'}>
                                            {importResult.summary.partialSuccess ? <AlertCircle className="w-4 h-4 text-amber-600" /> : <ShieldCheck className="w-4 h-4 text-emerald-600" />}
                                            <AlertTitle className={importResult.summary.partialSuccess ? 'text-amber-800' : 'text-emerald-800'}>
                                                {importResult.summary.partialSuccess ? 'Import completed with partial success' : 'Import completed'}
                                            </AlertTitle>
                                            <AlertDescription className={importResult.summary.partialSuccess ? 'text-amber-900' : 'text-emerald-900'}>
                                                {importResult.summary.created} contact{importResult.summary.created === 1 ? '' : 's'} created. Existing rows, duplicates, and blocked rows were skipped instead of being silently updated.
                                            </AlertDescription>
                                        </Alert>

                                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Created</div>
                                                <div className="text-3xl font-black tracking-tight mt-2">{importResult.summary.created}</div>
                                            </div>
                                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Skipped Existing</div>
                                                <div className="text-3xl font-black tracking-tight mt-2">{importResult.summary.skippedExisting}</div>
                                            </div>
                                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Skipped In Batch</div>
                                                <div className="text-3xl font-black tracking-tight mt-2">{importResult.summary.skippedDuplicateBatch}</div>
                                            </div>
                                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Blocked</div>
                                                <div className="text-3xl font-black tracking-tight mt-2">{importResult.summary.blockedError}</div>
                                            </div>
                                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Errors</div>
                                                <div className="text-3xl font-black tracking-tight mt-2">{importResult.summary.importErrors}</div>
                                            </div>
                                            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                                                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Warnings</div>
                                                <div className="text-3xl font-black tracking-tight mt-2">{importResult.summary.warningCount}</div>
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-border/60 bg-background overflow-x-auto">
                                            <table className="min-w-full text-sm">
                                                <thead className="bg-muted/30">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground">Row</th>
                                                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground">Result</th>
                                                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground">Notes</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-border/50">
                                                    {(importResult.rows || []).slice(0, 25).map((row) => (
                                                        <tr key={`result-${row.rowNumber}`}>
                                                            <td className="px-4 py-3 text-sm font-semibold text-foreground">{row.rowNumber}</td>
                                                            <td className="px-4 py-3">
                                                                <Badge variant={row.state === 'CREATED' ? 'success' : row.state === 'ERROR' ? 'destructive' : 'outline'}>
                                                                    {row.state}
                                                                </Badge>
                                                            </td>
                                                            <td className="px-4 py-3 text-xs text-muted-foreground">
                                                                {[...(row.reasons || []), ...(row.warnings || [])].join(' • ') || 'Processed without extra notes'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            <Card className="border-border/60 shadow-sm">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base font-black tracking-tight">
                                        <TableProperties className="w-4 h-4 text-primary" />
                                        Sample rows
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto rounded-2xl border border-border/60">
                                        <table className="min-w-full text-sm">
                                            <thead className="bg-muted/30">
                                                <tr>
                                                    {payload.header?.rawHeaders?.map((header, index) => (
                                                        <th key={`${header || 'column'}-${index}`} className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground">
                                                            {columns[index]?.resolvedLabel || header || `Column ${index + 1}`}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border/50">
                                                {payload.sampleRows?.map((row) => (
                                                    <tr key={`sample-row-${row.sampleIndex}`}>
                                                        {columns.map((column) => (
                                                            <td key={`${row.sampleIndex}-${column.resolvedLabel}`} className="px-4 py-3 text-sm text-foreground">
                                                                {row.values?.[column.resolvedLabel] || <span className="text-muted-foreground">-</span>}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="mt-3 text-xs text-muted-foreground font-medium">
                                        Sample values stay visible throughout mapping so users can confirm meaning instead of relying on header names alone.
                                    </div>
                                </CardContent>
                            </Card>

                            <Alert className="border-emerald-500/30 bg-emerald-500/5">
                                <Database className="w-4 h-4 text-emerald-600" />
                                <AlertTitle className="text-emerald-800">Shared import safety rules stay intact</AlertTitle>
                                <AlertDescription className="text-emerald-900">
                                    Auto-match stays suggestive, unresolved columns remain visible, preview is still required before execution, and every intake method uses the same create-only safety model.
                                </AlertDescription>
                            </Alert>
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
